import React, { useState } from "react";
import {
  Checkbox,
  Field,
  Input,
  Link,
  Radio,
  RadioGroup,
  Text,
} from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  type OneNoteLocation,
  useOneNoteSourceSettingsText,
  useOneNoteSourceSettingsViewModel,
} from "@onetasklist/shared/sources/onenote";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useConfig } from "@contexts/ConfigContext";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SourceSiteTagPicker } from "./components/SourceSiteTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";
import "./OneNoteSourceSettings.scss";

interface OneNoteSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const OneNoteSourceSettings: React.FC<OneNoteSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const { config } = useConfig();
  const regionBaseUrl = config?.regionBaseUrl ?? "";
  const text = useOneNoteSourceSettingsText(t);
  const vm = useOneNoteSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [groupQuery, setGroupQuery] = useState("");
  const [notebookQuery, setNotebookQuery] = useState("");
  const [sectionGroupQuery, setSectionGroupQuery] = useState("");
  const [sectionsQuery, setSectionsQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  const showNotebookSelection =
    vm.location === "Me" ||
    (vm.location === "SharePoint" &&
      vm.selectedSiteId !== vm.noneOptionValue) ||
    (vm.location === "Group" && vm.selectedGroupId !== vm.noneOptionValue);

  return (
    <>
      <SettingsSectionCard
        sectionOffset={sectionOffset}
        title={t(
          "board.connections.catalogue.sourceSettings.onenote.sectionLocation",
        )}
      >
        {vm.showNewConnectionMessage && (
          <Text>{text.newConnectionMessageText}</Text>
        )}
        {vm.errorMessage && <Text>{vm.errorMessage}</Text>}

        <Field orientation="horizontal" label={text.locationLabelText}>
          <RadioGroup
            value={vm.location}
            onChange={(_, data) =>
              vm.onSelectLocation(data.value as OneNoteLocation)
            }
          >
            <Radio value="Me" label={text.oneDriveLabelText} />
            <Radio value="SharePoint" label={text.sharePointLabelText} />
          </RadioGroup>
        </Field>

        {vm.location === "Group" && (
          <Field
            orientation="horizontal"
            label={text.groupLabelText}
            hint={text.groupHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedGroupId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedGroupId
              }
              selectedLabel={
                vm.groups.find((group) => group.id === vm.selectedGroupId)
                  ?.displayName
              }
              query={groupQuery}
              placeholder={text.notSetText}
              options={vm.groups.map((group) => ({
                id: group.id,
                label: group.displayName || group.id,
              }))}
              onQueryChange={setGroupQuery}
              onSelect={vm.onSelectGroup}
              clearSelectionText={text.notSetText}
              isLoading={!vm.groupsLoaded}
            />
          </Field>
        )}

        {vm.location === "SharePoint" && (
          <Field
            orientation="horizontal"
            label={text.siteLabelText}
            hint={`${text.siteHintText} ${text.siteSearchMinCharsText}`}
          >
            <SourceSiteTagPicker
              selectedSiteId={
                vm.selectedSiteId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedSiteId
              }
              selectedSiteLabel={vm.selectedSiteDisplayName || text.notSetText}
              query={vm.siteQuery}
              placeholder={text.siteSearchPlaceholderText}
              options={vm.sites.map((site) => ({
                id: site.id,
                label: site.displayName || site.id,
                secondaryLabel: site.webUrl,
              }))}
              onQueryChange={vm.onSiteQueryChange}
              onSelectSite={vm.onSelectSite}
              typeToSearchText={text.siteSearchMinCharsText}
              clearSelectionText={text.notSetText}
              isLoading={vm.searchingSites}
            />
            {vm.siteSearchFailed && <Text>{text.siteSearchFailedText}</Text>}
          </Field>
        )}

        {vm.location === "SharePoint" && vm.selectedSiteWebUrl && (
          <Field orientation="horizontal" label={text.siteUrlLabelText}>
            <a href={vm.selectedSiteWebUrl} target="_blank" rel="noreferrer">
              {vm.selectedSiteWebUrl}
            </a>
          </Field>
        )}

        {showNotebookSelection && (
          <Field
            orientation="horizontal"
            label={text.notebookLabelText}
            hint={text.notebookHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedNotebookId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedNotebookId
              }
              selectedLabel={
                vm.notebooks.find(
                  (notebook) => notebook.id === vm.selectedNotebookId,
                )?.displayName
              }
              query={notebookQuery}
              placeholder={text.notebookPlaceholderText}
              options={vm.notebooks.map((notebook) => ({
                id: notebook.id,
                label: notebook.displayName || notebook.id,
              }))}
              onQueryChange={setNotebookQuery}
              onSelect={vm.onSelectNotebook}
              clearSelectionText={text.notebookPlaceholderText}
              isLoading={!vm.notebooksLoaded}
            />
          </Field>
        )}
      </SettingsSectionCard>

      {vm.selectedNotebookId !== vm.noneOptionValue && (
        <SettingsSectionCard
          sectionOffset={sectionOffset + 1}
          title={t(
            "board.connections.catalogue.sourceSettings.onenote.sectionContent",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.sectionGroupLabelText}
            hint={text.sectionGroupHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedSectionGroupId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedSectionGroupId
              }
              selectedLabel={
                vm.sectionGroups.find(
                  (sectionGroup) =>
                    sectionGroup.id === vm.selectedSectionGroupId,
                )?.displayName
              }
              query={sectionGroupQuery}
              placeholder={text.sectionGroupPlaceholderText}
              options={vm.sectionGroups.map((sectionGroup) => ({
                id: sectionGroup.id,
                label: sectionGroup.displayName || sectionGroup.id,
              }))}
              onQueryChange={setSectionGroupQuery}
              onSelect={vm.onSelectSectionGroup}
              clearSelectionText={text.sectionGroupPlaceholderText}
              isLoading={!vm.sectionGroupsLoaded}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.sectionsLabelText}
            hint={text.sectionsHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedSectionIds}
              query={sectionsQuery}
              placeholder={text.sectionsPlaceholderText}
              options={vm.sections.map((section) => ({
                id: section.id,
                label: section.displayName || section.id,
              }))}
              onQueryChange={setSectionsQuery}
              onSelectionChange={vm.onSelectSections}
              minQueryLength={0}
              typeToSearchText={text.sectionsHintText}
              isLoading={!vm.sectionsLoaded}
            />
          </Field>

          {/* Tags use JSX label on <Checkbox> — grid layout constraint, not a fixable case */}
          <Field
            orientation="horizontal"
            label={text.tagsLabelText}
            hint={text.tagsHintText}
          >
            <div className="onenote-source-settings__tags">
              {vm.tagGroups.map((group) => (
                <section
                  key={group.id}
                  className="onenote-source-settings__tag-group"
                >
                  <div className="onenote-source-settings__tag-group-header">
                    <Text weight="semibold">
                      {text.getTagGroupLabelText(group.id)}
                    </Text>
                    <Text
                      size={200}
                      className="onenote-source-settings__tag-group-count"
                    >
                      {group.selectedCount}/{group.tags.length}
                    </Text>
                    <Link
                      as="button"
                      className="onenote-source-settings__tag-group-toggle"
                      onClick={() =>
                        vm.onToggleTagGroup(group.id, !group.isAllSelected)
                      }
                    >
                      {group.isAllSelected
                        ? text.tagsClearText
                        : text.tagsSelectAllText}
                    </Link>
                  </div>
                  <div className="onenote-source-settings__tag-grid">
                    {group.tags.map((tag) => (
                      <Checkbox
                        key={tag}
                        checked={Boolean(vm.selectedTags[tag])}
                        onChange={(_, data) =>
                          vm.onToggleTag(tag, Boolean(data.checked))
                        }
                        label={
                          <span className="onenote-source-settings__tag-label">
                            <img
                              className="onenote-source-settings__tag-icon"
                              src={`${regionBaseUrl}/assets/img/sourceicons/onenotetagicons/${tag}.png`}
                              alt=""
                            />
                            {text.getTagLabelText(tag)}
                          </span>
                        }
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </Field>

          <Field
            orientation="horizontal"
            label={text.filterStringLabelText}
            hint={text.filterStringHintText}
          >
            <Input
              value={vm.filterString}
              onChange={(_, data) => vm.onFilterStringChange(data.value)}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.includeBulletedListsText}
            hint={text.includeBulletedListsHintText}
          >
            <Checkbox
              checked={vm.includeBulletedLists}
              onChange={(_, data) =>
                vm.onToggleIncludeBulletedLists(Boolean(data.checked))
              }
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.includeTablesText}
            hint={text.processingWarningText}
          >
            <Checkbox
              checked={vm.includeTables}
              onChange={(_, data) =>
                vm.onToggleIncludeTables(Boolean(data.checked))
              }
            />
          </Field>

          <Text>{text.dontProcessHintText}</Text>
        </SettingsSectionCard>
      )}

      {(!vm.groupsLoaded ||
        !vm.notebooksLoaded ||
        !vm.sectionGroupsLoaded ||
        !vm.sectionsLoaded ||
        vm.searchingSites) && <Text>{text.loadingText}</Text>}

      {vm.hasError && !vm.errorMessage && <Text>{text.loadErrorText}</Text>}
    </>
  );
};
